import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import { ReauthModal } from '../components/Layout/ReauthModal';

interface ReauthState {
  token: string | null;
  expiresAt: Date | null;
  isActive: boolean;
  remainingSeconds: number;
}

interface ReauthContextType {
  state: ReauthState;
  requestReauth: () => Promise<string | null>;
  getReauthToken: () => string | null;
  clearReauth: () => void;
  isReauthValid: () => boolean;
}

const ReauthContext = createContext<ReauthContextType | null>(null);

const STORAGE_KEY = 'tark_admin_reauth';

export function ReauthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ReauthState>({
    token: null,
    expiresAt: null,
    isActive: false,
    remainingSeconds: 0,
  });

  const [showModal, setShowModal] = useState(false);
  const [pendingResolve, setPendingResolve] = useState<((token: string | null) => void) | null>(null);

  // Load from storage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        const expiresAt = new Date(data.expiresAt);
        if (expiresAt > new Date()) {
          setState({
            token: data.token,
            expiresAt,
            isActive: true,
            remainingSeconds: Math.floor((expiresAt.getTime() - Date.now()) / 1000),
          });
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Update remaining time countdown
  useEffect(() => {
    if (!state.isActive || !state.expiresAt) return;

    const interval = setInterval(() => {
      const remaining = Math.floor((state.expiresAt!.getTime() - Date.now()) / 1000);
      if (remaining <= 0) {
        clearReauth();
      } else {
        setState((prev) => ({ ...prev, remainingSeconds: remaining }));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [state.isActive, state.expiresAt]);

  const saveToken = useCallback((token: string, expiresInSeconds: number) => {
    const expiresAt = new Date(Date.now() + expiresInSeconds * 1000);
    const newState = {
      token,
      expiresAt,
      isActive: true,
      remainingSeconds: expiresInSeconds,
    };
    setState(newState);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, expiresAt: expiresAt.toISOString() }));
  }, []);

  const clearReauth = useCallback(() => {
    setState({
      token: null,
      expiresAt: null,
      isActive: false,
      remainingSeconds: 0,
    });
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const isReauthValid = useCallback(() => {
    if (!state.token || !state.expiresAt) return false;
    return state.expiresAt > new Date();
  }, [state.token, state.expiresAt]);

  const getReauthToken = useCallback(() => {
    if (isReauthValid()) {
      return state.token;
    }
    return null;
  }, [state.token, isReauthValid]);

  const requestReauth = useCallback((): Promise<string | null> => {
    // If already valid, return existing token
    if (isReauthValid()) {
      return Promise.resolve(state.token);
    }

    // Show modal and wait for result
    return new Promise((resolve) => {
      setPendingResolve(() => resolve);
      setShowModal(true);
    });
  }, [state.token, isReauthValid]);

  const handleReauthSuccess = useCallback(
    (token: string, expiresInSeconds: number) => {
      saveToken(token, expiresInSeconds);
      setShowModal(false);
      if (pendingResolve) {
        pendingResolve(token);
        setPendingResolve(null);
      }
    },
    [saveToken, pendingResolve]
  );

  const handleReauthCancel = useCallback(() => {
    setShowModal(false);
    if (pendingResolve) {
      pendingResolve(null);
      setPendingResolve(null);
    }
  }, [pendingResolve]);

  return (
    <ReauthContext.Provider
      value={{
        state,
        requestReauth,
        getReauthToken,
        clearReauth,
        isReauthValid,
      }}
    >
      {children}
      <ReauthModal
        isOpen={showModal}
        onSuccess={handleReauthSuccess}
        onCancel={handleReauthCancel}
      />
    </ReauthContext.Provider>
  );
}

export function useReauth() {
  const context = useContext(ReauthContext);
  if (!context) {
    throw new Error('useReauth must be used within a ReauthProvider');
  }
  return context;
}

/**
 * Hook for executing danger zone operations with automatic reauth handling
 */
export function useDangerZone() {
  const { requestReauth, getReauthToken } = useReauth();

  const executeDangerZone = useCallback(
    async <T,>(
      operation: (headers: Record<string, string>) => Promise<T>
    ): Promise<T> => {
      // Request reauth if needed
      const token = await requestReauth();
      if (!token) {
        throw new Error('Re-authentication cancelled');
      }

      // Execute with reauth header
      return operation({ 'X-Admin-Reauth': token });
    },
    [requestReauth]
  );

  return { executeDangerZone, getReauthToken };
}
